import { IconButton, Stack, useTheme } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import MaterialReceptionHeader from './MaterialReceptionHeader'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { enumFilterOptions, systemValueLabel } from '../generic/systemValues/systemValueLabel'
import AlertUI from '../generic/AlertUI'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { useState, useEffect } from 'react'
import { apiPut } from '../generic/Api_Functions'
import { TextSnippetOutlined } from '@mui/icons-material'
import Document from '../configuration/document/Document'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import SmartTextField from '../generic/smarttextfield/SmartTextField'
import { DefaultAccordion } from '../generic/DefaultAccordion'
import WarningIcon from '@mui/icons-material/Warning'
import { useNavigate } from 'react-router-dom'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import { getMaterialReceptionColumns } from './MaterialReceptionExtendedColumnsUtil'

/**
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNoteDataAction} DeliveryNoteDataAction DataAction object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').DeliveryNote} DeliveryNote Data object
 * @typedef {import('../generic/utilities/objectStructures/materialreception/materialReception').PurchaseOrderTheoreticalReception} PurchaseOrderTheoreticalReception PurchaseOrderTheoreticalReception object
 */

/**
 *
 * @param {object} props
 * @param {DeliveryNote} props.deliveryNote
 * @param {React.Dispatch<DeliveryNoteDataAction>} props.dispatchDeliveryNote
 * @param {string} props.lastQrVersion
 * @returns {JSX.Element}
 */
const ReportTheoreticalQuantity = ({ deliveryNote, dispatchDeliveryNote, lastQrVersion, rawMaterialExtendedColumns }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  /** @type {[PurchaseOrderTheoreticalReception[], React.Dispatch<SetStateAction<PurchaseOrderTheoreticalReception[]>>]} */
  const [reportedData, setReportedData] = useState(deliveryNote?.purchaseOrderTheoreticalReceptions ?? [])
  const [showDocument, setShowDocument] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const theme = useTheme()
  const [theoreticalRow, setDataRow] = useState(null)
  const isUserAuthorized = useAuthorization()
  useEffect(() => {
    setReportedData(deliveryNote?.purchaseOrderTheoreticalReceptions ?? [])
  }, [deliveryNote])

  const style = {
    minWidth: '80rem',
    width: '100%',
    display: 'grid',
    gap: '1rem',
    backgroundColor: theme.palette.background.row2,
    padding: '1rem'
  }
  const navigate = useNavigate()

  const columns = [
    {
      name: t_('Status'),
      field: 'status',
      render: (row) => systemValueLabel(t_, 'PurchaseOrderTheoreticalReceptionStatusEnum', row.status),
      filterByLabel: { options: enumFilterOptions(t_, 'PurchaseOrderTheoreticalReceptionStatusEnum') }
    },
    {
      name: t_('Supplier code'),
      field: 'supplier.code'
    },
    {
      name: t_('Supplier name'),
      field: 'supplier.name'
    },
    {
      name: t_('Record code'),
      field: 'recordCode'
    },
    {
      name: t_('Order'),
      field: 'purchaseOrder.name'
    },
    {
      name: t_('Pos / Line'),
      field: 'lineOrderPosition'
    },
    {
      name: t_('Serializable'),
      field: 'serializable',
      render: (row) => {
        return row.serializable ? <CheckIcon color='primary' /> : ''
      },
      readOnly: true
    },
    {
      name: t_('Raw material reference'),
      field: 'rawMaterialReference'
    },
    {
      name: t_('Revision'),
      field: 'revision'
    },
    {
      name: t_('Project'),
      field: 'projectCode',
      readOnly: true
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Received theoretical quantity'),
      field: 'receivedTheoreticalQuantity',
      render: (row) => {
        if (row.locked || !deliveryNote?.id) {
          return row.receivedTheoreticalQuantity
        }

        const purchaseOrderTheoreticalReception = reportedData.find(data => data.id === row.id)

        return (
          <SmartTextField
            value={purchaseOrderTheoreticalReception?.receivedTheoreticalQuantity == null ? '' : purchaseOrderTheoreticalReception?.receivedTheoreticalQuantity}
            onChange={(event) => {
              const newData = [...reportedData]
              const index = newData.findIndex(data => data.id === row.id)
              newData[index].receivedTheoreticalQuantity = event
              setReportedData(newData)
            }}
            checkError={(value) => {
              if (!value) {
                return ''
              }
              if (isNaN(value) || Number(value) <= 0) {
                return t_('Theoretical quantity must be a number greater than 0')
              }
              return ''
            }}
          />
        )
      }
    }
  ]
  const getColumns = () =>
    getMaterialReceptionColumns({
      columns,
      extendedColumns: rawMaterialExtendedColumns,
      editIndex: null
    })
  const onSave = async (number, date) => {
    let correctFormat = true
    reportedData.forEach((element) => {
      if (!element.receivedTheoreticalQuantity) {
        return
      }
      if ((isNaN(element.receivedTheoreticalQuantity) || Number(element.receivedTheoreticalQuantity <= 0))) {
        correctFormat = false
      }
    })

    if (!correctFormat) {
      showAlert({ severity: 'error', title: t_('There are some incorrect values. Fix them before saving') })
      return false
    }

    const data = { ...deliveryNote }
    data.date = date
    data.number = number
    data.purchaseOrderTheoreticalReceptions = reportedData
    const result = await apiPut('deliveryNote/update/' + lastQrVersion + '/' + false, data)
    dispatchDeliveryNote({ type: 'SET_DELIVERY_NOTE', deliveryNote: result })
    setReportedData(result.purchaseOrderTheoreticalReceptions)
    return true
  }

  const actions = {
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        onClick: (row) => {
          const data = { id: row.id, name: 'Order: ' + row.purchaseOrder?.id + '  ||   Line: ' + row.lineOrderPosition }
          setDataRow(data)
          setShowDocument(true)
        },
        disabled: !isUserAuthorized('DOCUMENTS_PURCHASE_ORDER_THEORETICAL_RECEPTION')

      },
      {
        name: 'Incidence',
        render: (row) => {
          return (
            <IconButton>
              <WarningIcon />
            </IconButton>
          )
        },
        onClick: (row) => {
          navigate('/mos/reception/incidence', { state: { deliveryNote, type: 'THEORETICAL', currentSelectedId: row.id, window: 'THEORETICAL_QUANTITY' } })
        },
        disabled: (row) => !isUserAuthorized('RECEPTION_INCIDENCE') || row.type === 'MANUAL'
      }
    ]
  }

  const renderAccordion = (header, reference, defaultExpanded, resourceType) => {
    return (
      <DefaultAccordion headerRender={t_(header)} defaultExpanded={defaultExpanded} disabled={!isUserAuthorized('DOCUMENTS_' + resourceType)}>
        <Document reference={reference} resourceType={resourceType} isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />
      </DefaultAccordion>
    )
  }
  const dataFetch = async (requestPagination) => {
    if (deliveryNote?.id) {
      return reportedData
    }
    return await apiPut('purchaseOrderTheoreticalReception', requestPagination)
  }

  return (
    <Stack>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={
          <div style={style}>
            {renderAccordion('Delivery Note', { id: deliveryNote?.id, name: deliveryNote?.number }, false, 'DELIVERY_NOTE')}
            {renderAccordion('Theoretical Quantity', theoreticalRow, true, 'PURCHASE_ORDER_THEORETICAL_RECEPTION')}
            {/* {renderAccordion('Real Quantity', realRow, false, 'PURCHASE_ORDER_REAL_RECEPTION')} */}
            {/* {renderAccordion('Qualitative Quantity', qualitativeRow, false, 'PURCHASE_ORDER_QUALITATIVE_RECEPTION')} */}
          </div>
        }
        setOpen={showDocument}
      />
      {AlertElement}
      <MaterialReceptionHeader
        deliveryNote={deliveryNote}
        dispatchDeliveryNote={dispatchDeliveryNote}
        showAlert={showAlert}
        onSave={(number, date) => onSave(number, date)}
        window='THEORETICAL_QUANTITY'
      />
      <div style={{ marginTop: '1rem' }}>
        <SmartMateriaUITable
          tableTuneUniqueKey='ReportTheoreticalQuantity'
          columns={getColumns()}
          dataFetch={dataFetch}
          actions={actions}
          stickyTopOffset={112}
          pageable={deliveryNote?.id ? undefined : true}
        />
      </div>
    </Stack>
  )
}

export default ReportTheoreticalQuantity
